import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import type { CompanyRow, CursorPage, GstinLookup, Industry, PincodeLookup, PlatformOverview, TenantStatus } from '@dru/types';
import {
  ONBOARDING_LABEL, SETUP_STATUS_LABEL, TENANT_STATUS_LABEL,
} from '@dru/types';
import {
  useToast,
  addressForm, addressPayload, Button, DataTable, Field, FilterBar, IconAction, InfoTip, Input, RowActions, SearchInput,
  StatusBadge, type Tone, TextField, Alert, useIdempotencyKey, type AddressFormValue,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { AddressFields } from '../../../components/AddressFields';
import { IndustryCombobox } from '../../../components/IndustryCombobox';
import { usePages } from '../../../lib/usePages';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';

/**
 * The company registry.
 *
 * A registration is not a company. It arrives PENDING with nobody in it and no
 * way in, and becomes usable only when somebody here reviews it — which is why
 * the review is a screen with the paperwork in front of it rather than a
 * status dropdown.
 *
 * Reads as a list of customers rather than a database table: what needs
 * doing first, then search and two filters, then rows a reader can scan —
 * who, where, how big, what state. A row opens the company. Reading inside a
 * company's own records is a privileged act with its own justification, and
 * it is offered on the company's page under that name, not as a second link
 * on every row.
 */
export function Companies() {
  // Status lives in the URL rather than in component state, so the dashboard
  // can link at "the pending registrations" and a colleague can be sent the
  // same list. `replace` rather than `push`: changing a filter is refining one
  // question, not asking a second, and Back should leave the screen.
  const nav = useNavigate();
  const { permissions } = useAuth();
  const canRegister = holds(permissions, 'tenant.create');
  // The waiting count is the server's, not a count of the loaded page.
  const canCount = holds(permissions, 'crosstenant.tenant.read');
  const overview = useQuery<PlatformOverview | null>(
    () => (canCount ? api.get<PlatformOverview>('/platform/overview').catch(() => null) : Promise.resolve(null)), [canCount]);
  const waiting = overview.data?.tenants.PENDING ?? 0;
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const industry = params.get('industry') ?? '';
  const setFilters = (next: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  };
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ kind: 'register' } | null>(null);
  const { toast } = useToast();

  const companies = usePages<CompanyRow>(
    (cursor) => api.get<CursorPage<CompanyRow>>(
      `/tenants?limit=100${status ? `&status=${status}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}${cursor ? `&cursor=${cursor}` : ''}`,
    ),
    [status, search],
  );

  const loaded = companies.items;

  // Industry is narrowed here, over the rows loaded so far, because the
  // registry API does not take it. The options are the industries actually
  // present, so the menu never offers a choice that empties the list; a
  // narrowed list still offers "Load more", which brings in the next page and
  // re-applies the filter.
  const industries = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of loaded) {
      const name = c.industryRef?.name ?? c.industry;
      if (name) seen.set(c.industryRef?.id ?? name, name);
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [loaded]);
  const rows = industry
    ? loaded.filter((c) => (c.industryRef?.id ?? c.industry) === industry)
    : loaded;

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Companies"
        hint="Every customer account: registrations awaiting approval, and the companies already running."
        actions={canRegister ? <Button onClick={() => setDialog({ kind: 'register' })}>Register a company</Button> : undefined}
      />


      {/* Self-registered companies wait here, and nothing else asks for them. */}
      {waiting > 0 && status !== 'PENDING' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-dueSoon-600/30 bg-dueSoon-100 px-4 py-3">
          <div className="min-w-0">
            <div className="text-body font-semibold text-neutral-900">
              {waiting} {waiting === 1 ? 'registration is' : 'registrations are'} waiting for review
            </div>
            <p className="text-small text-neutral-700">Registered from the public sign-up form. Nobody there can sign in until one of us approves.</p>
          </div>
          <Button variant="secondary" onClick={() => setFilters({ status: 'PENDING' })}>Review registrations</Button>
        </div>
      )}

      <FilterBar
        filters={[
          {
            key: 'status', label: 'Status',
            options: (['PENDING', 'ACTIVE', 'SUSPENDED', 'CLOSED'] as TenantStatus[])
              .map((s) => ({ value: s, label: TENANT_STATUS_LABEL[s] })),
          },
          { key: 'industry', label: 'Industry', options: industries },
        ]}
        value={{ status: status || undefined, industry: industry || undefined }}
        onChange={setFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search by name or GSTIN"
          aria-label="Search companies"
          className="w-full sm:w-72"
        />
      </FilterBar>

      <DataTable<CompanyRow>
        rows={rows}
        loading={companies.loading}
        error={companies.error}
        onRetry={companies.reload}
        onLoadMore={companies.loadMore}
        hasMore={companies.hasMore}
        loadingMore={companies.loadingMore}
        rowKey={(c) => c.id}
        onRowClick={(c) => nav(`/admin/companies/${c.id}`)}
        empty={{
          title: search || status || industry ? 'No companies match' : 'No companies yet',
          hint: search || status || industry
            ? 'Try a different name, or clear the filters.'
            : canRegister ? 'Register one to start onboarding, or convert an enquiry from Leads.' : 'Companies appear here once Operations registers them.',
        }}
        columns={[
          {
            key: 'name', header: 'Company', alwaysVisible: true,
            cell: (c) => (
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900">{c.name}</div>
                <div className="truncate text-caption font-normal text-neutral-500">
                  {[
                    [c.city, c.state].filter(Boolean).join(', ') || null,
                    c.industryRef?.name ?? c.industry,
                  ].filter(Boolean).join(' · ') || (c.legalName ?? 'Location not recorded')}
                </div>
              </div>
            ),
          },
          {
            key: 'size', header: 'Workforce',
            cell: (c) => <Size company={c} />,
          },
          {
            key: 'status', header: 'Status', mobileLabel: false,
            cell: (c) => <StatusBadge tone={statusTone(c.status)}>{TENANT_STATUS_LABEL[c.status]}</StatusBadge>,
          },
          {
            key: 'setup', header: 'Setup',
            cell: (c) => <SetupState company={c} />,
          },
          {
            key: 'created', header: 'Registered', align: 'right', hideOnMobile: true,
            cell: (c) => (
              <span className="text-caption font-normal tabular-nums text-neutral-600">
                {formatDate(c.createdAt)}
              </span>
            ),
          },
          {
            key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (c) => (
              // The one thing a row can need from the reader: a registration
              // waiting for its review. It opens the same page the row does,
              // and says why. Every other row just opens.
              <RowActions>
                {c.status === 'PENDING'
                  ? <IconAction label={`Review the registration of ${c.name}`} icon={Eye} primary onClick={() => nav(`/admin/companies/${c.id}`)} />
                  : <IconAction label={`View ${c.name}`} icon={Eye} onClick={() => nav(`/admin/companies/${c.id}`)} />}
              </RowActions>
            ),
          },
        ]}
      />

      {dialog?.kind === 'register' && (
        <RegisterDialog
          onClose={() => setDialog(null)}
          onDone={(r) => {
            setDialog(null);
            toast(r.failed ? 'warning' : 'success', r.failed
              ? `${r.name} is live, but the welcome email was not sent: ${r.failed} Resend it from the company page.`
              : r.email ? `${r.name} is live. HR invitation sent to ${r.email}.` : `${r.name} is live. HR access pending — invite HR from company setup.`);
            // Straight into the new company's configuration: setting it up is
            // the next job, and the list was a dead end (flow audit 1.3).
            nav(`/admin/companies/${r.id}?tab=setup`);
          }}
        />
      )}
    </div>
  );
}

/**
 * How big the company is, on two lines.
 *
 * Plants and logins are counted only for a reader who may see inside the
 * company; a platform session reads the registration and nothing under it,
 * and the API sends null rather than zero for that. Workers fall back to
 * the figure stated at registration, and say so.
 */
function Size({ company: c }: { company: CompanyRow }) {
  const count = c.employees ?? c.workforceCount;
  const workers = count != null && count > 0
    ? `${count.toLocaleString('en-IN')} employees`
    : c.workforceBand ? `${c.workforceBand} employees (stated)` : null;
  const plants = c.plants != null ? `${c.plants} plant${c.plants === 1 ? '' : 's'}` : null;
  if (!workers && !plants) {
    return <span className="text-caption font-normal text-neutral-500">Not stated</span>;
  }
  return (
    <div className="text-caption font-normal">
      <div className="tabular-nums text-neutral-800">{workers ?? plants}</div>
      {workers && plants && <div className="tabular-nums text-neutral-500">{plants}</div>}
    </div>
  );
}

/**
 * Where the company is in getting set up, or what is holding it before that.
 *
 * The figure is the one recorded on the company. The API keeps it in step
 * with what exists — at registration, after every change made from the
 * company's Configuration tab, and whenever that tab is read — so it agrees
 * with the company's own page rather than waiting for somebody to mark it.
 */
function SetupState({ company: c }: { company: CompanyRow }) {
  if (c.status === 'PENDING') {
    return (
      <div className="text-caption font-normal">
        <div className="font-medium text-neutral-900">Self-registered · needs review</div>
        <div className="text-neutral-500">Paperwork: {(ONBOARDING_LABEL[c.onboardingState] ?? c.onboardingState).toLowerCase()}</div>
      </div>
    );
  }
  if (c.status === 'CLOSED') {
    return (
      <span className="text-caption font-normal text-neutral-500">
        {c.onboardingState?.startsWith('REJECTED') ? 'Registration rejected' : 'Account closed'}
      </span>
    );
  }
  const s = c.setupStatus ?? 'NOT_STARTED';
  return (
    <div className="text-caption font-normal">
      <div className={s === 'COMPLETED' ? 'text-neutral-800' : 'font-medium text-neutral-900'}>{SETUP_STATUS_LABEL[s]}</div>
      {c.setupMode === 'SUPPORT_ASSISTED' && <div className="text-neutral-500">Support-assisted</div>}
    </div>
  );
}

function statusTone(s: TenantStatus): Tone {
  switch (s) {
    case 'ACTIVE': return 'covered';
    case 'PENDING': return 'dueSoon';
    case 'SUSPENDED': return 'overdue';
    case 'CLOSED': return 'neutral';
    default: return 'neutral';
  }
}

/**
 * Registration, as one page: Company, Contact and Location. Save sends it —
 * one POST, under one idempotency key — and opens the new company's
 * Configuration tab. What saving does is said beside the button, so there is
 * no second "Confirm and save" (flow audit 1.3). The paperwork state is not
 * asked: activation records it as Onboarded whatever was chosen (1.4).
 *
 * The GSTIN can fetch the company from GST. What comes back fills the
 * fields that are empty or still hold the last fetch's value; anything the
 * operator typed over is kept and named. The company is recorded as
 * GST-verified only when the details were fetched for the GSTIN being
 * saved, and even then the server decides from its own record of the
 * lookup, not from this form.
 *
 * The pincode is required: the first plant and the primary location are
 * made from it, so HR can add employees on day one (flow audit 1.2). The
 * industry is optional and only suggests packages later (1.5). Workforce
 * band, employee-ID prefix and setup mode are not asked (docs/38 D2, D3):
 * the API derives the prefix and starts every new company support-assisted.
 */
type Section = 'company' | 'contact' | 'location';
const FIELD_SECTION: Record<string, Section> = {
  name: 'company', legalName: 'company', gstin: 'company', workforceCount: 'company',
  industryId: 'company',
  contactName: 'contact', contactDesignation: 'contact', contactPhone: 'contact',
  contactAltPhone: 'contact', contactEmail: 'contact', website: 'contact',
};

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/;
const normaliseGstin = (v: string) => v.replace(/\s+/g, '').toUpperCase();
const digits10 = (v: string) => v.replace(/\D/g, '').slice(0, 10);

/** The address fields a GST lookup can return, in the form's own keys. */
const GST_ADDRESS_KEYS = [
  'addressLine1', 'addressLine2', 'locality', 'landmark', 'city', 'district', 'state', 'pincode',
] as const;
const FILL_LABEL: Record<string, string> = {
  name: 'Company name', legalName: 'Legal name', addressLine1: 'Address line 1',
  addressLine2: 'Address line 2', locality: 'Locality', landmark: 'Landmark', city: 'City',
  district: 'District', state: 'State', pincode: 'Pincode',
};

type GstState = {
  phase: 'idle' | 'loading' | 'done' | 'error';
  lookup: GstinLookup | null;
  /** Which GSTIN `lookup` answers. Editing the box away from it makes the details stale. */
  fetchedFor: string | null;
  /** What the last fetch wrote into each field, so a repeat fetch knows what it may replace. */
  filled: Record<string, string>;
  kept: string[];
  error: { kind: 'invalid' | 'notFound' | 'unavailable'; message: string } | null;
};
const GST_IDLE: GstState = { phase: 'idle', lookup: null, fetchedFor: null, filled: {}, kept: [], error: null };

function RegisterDialog({ onClose, onDone }: {
  onClose: () => void;
  onDone: (r: { id: string; name: string; email: string; failed: string | null }) => void;
}) {
  const [form, setForm] = useState({
    name: '', legalName: '', gstin: '', workforceCount: '',
    contactName: '', contactDesignation: '', contactPhone: '', contactAltPhone: '',
    contactEmail: '', website: '',
  });
  const [addHr, setAddHr] = useState(false);
  const [hr, setHr] = useState({ fullName: '', email: '' });
  const [address, setAddress] = useState<AddressFormValue>(addressForm(null));
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [gst, setGst] = useState<GstState>(GST_IDLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [scrollTick, setScrollTick] = useState(0);
  const idem = useIdempotencyKey();
  const formRef = useRef<HTMLDivElement>(null);

  // The response to a fetch lands after an await; it must merge into what is
  // on screen then, not what was there when the button was pressed.
  const latest = useRef({ form, address });
  latest.current = { form, address };

  // Only someone who may register or edit a company reaches this dialog, and
  // that is exactly who the status route admits.
  const gstConfig = useQuery<{ configured: boolean }>(
    () => api.get<{ configured: boolean }>('/geo/gstin/status').catch(() => ({ configured: false })),
    [],
  );
  const gstAvailable = gstConfig.data?.configured === true;

  const currentGstin = normaliseGstin(form.gstin);
  const [pinNote, setPinNote] = useState<string | null>(null);
  const places = useQuery<{ configured: boolean }>(
    () => api.get<{ configured: boolean }>('/geo/places/status').catch(() => ({ configured: false })),
    [],
  );
  const verified = Boolean(gst.lookup && gst.fetchedFor === currentGstin);
  const stale = Boolean(gst.lookup && gst.fetchedFor !== currentGstin);

  const set = (k: keyof typeof form) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  };

  // After errors are shown, bring the first one into view: on one long page
  // the field that failed is usually not the one the button sits beside.
  useEffect(() => {
    if (!scrollTick) return;
    const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (first) {
      first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      first.focus({ preventScroll: true });
    }
  }, [scrollTick]);

  // Client-side checks are for the person typing; the server is the authority
  // and its messages are shown in exactly the same place.
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (addHr && hr.fullName.trim().length < 2) e.hrName = 'Enter the HR name';
    if (addHr && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(hr.email)) e.hrEmail = 'Enter a valid HR email';
    if (form.name.trim().length < 2) e.name = 'Enter the company name (at least 2 characters)';
    if (form.contactName.trim().length < 2) e.contactName = 'Enter the primary business contact name';
    if (!form.contactEmail.trim() && !form.contactPhone && !form.contactAltPhone) e.contactEmail = 'Enter an email or mobile number for company communication';
    if (form.gstin && !GSTIN_RE.test(currentGstin)) e.gstin = 'Enter a valid 15-character GSTIN';
    if (form.workforceCount && !/^\d+$/.test(form.workforceCount)) e.workforceCount = 'Enter a whole number';
    if (form.contactPhone && !/^[6-9]\d{9}$/.test(form.contactPhone)) e.contactPhone = 'Enter a valid mobile number';
    if (form.contactAltPhone && !/^[6-9]\d{9}$/.test(form.contactAltPhone)) e.contactAltPhone = 'Enter a valid mobile number';
    if (form.contactEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail)) e.contactEmail = 'Enter a valid email address';
    if (form.website && !/^https?:\/\//.test(form.website)) e.website = 'Start with https://';
    if (!address.pincode) e.pincode = 'Enter the pincode — the first plant is created from it';
    else if (address.pincode.length !== 6) e.pincode = 'Enter a valid 6-digit Indian pincode';
    return e;
  }

  async function fetchGst() {
    const gstin = currentGstin;
    setForm((f) => ({ ...f, gstin }));
    if (!GSTIN_RE.test(gstin)) {
      setFieldErrors((e) => ({ ...e, gstin: 'Enter a valid 15-character GSTIN to fetch its details' }));
      return;
    }
    setFieldErrors((e) => { const { gstin: _, ...rest } = e; return rest; });
    setGst((g) => ({ ...g, phase: 'loading', error: null }));
    try {
      const lookup = await api.get<GstinLookup>(`/geo/gstin/${encodeURIComponent(gstin)}`);
      applyLookup(gstin, lookup);
    } catch (e) {
      const type = e instanceof ApiError ? e.type : '';
      const kind = type === 'invalid-gstin' ? 'invalid' : type === 'gstin-not-found' ? 'notFound' : 'unavailable';
      setGst((g) => ({ ...g, phase: 'error', error: { kind, message: errorMessage(e) } }));
    }
  }

  /**
   * Fills only what GST returned, and only where the box is empty or still
   * holds what the previous fetch put there. A value somebody typed is never
   * replaced; it is listed instead, so they can decide.
   */
  function applyLookup(gstin: string, r: GstinLookup) {
    const { form: f, address: a } = latest.current;
    const filled: Record<string, string> = { ...gst.filled };
    const kept: string[] = [];
    const take = (key: string, current: string, incoming: string | undefined): string | null => {
      if (!incoming) return null;
      const cur = current.trim();
      if (!cur || cur === filled[key] || cur === incoming) {
        filled[key] = incoming;
        return incoming;
      }
      kept.push(FILL_LABEL[key] ?? key);
      return null;
    };

    const nextForm = { ...f };
    const name = take('name', f.name, r.tradeName ?? r.legalName);
    if (name) nextForm.name = name;
    const legal = take('legalName', f.legalName, r.legalName);
    if (legal) nextForm.legalName = legal;

    const nextAddress = { ...a };
    for (const k of GST_ADDRESS_KEYS) {
      const v = take(k, a[k] ?? '', r.address[k]);
      if (v) nextAddress[k] = v;
    }

    setForm(nextForm);
    setAddress(nextAddress);
    setFieldErrors((e) => {
      const rest = { ...e };
      for (const k of Object.keys(filled)) delete rest[k];
      return rest;
    });
    setGst({ phase: 'done', lookup: r, fetchedFor: gstin, filled, kept, error: null });

    void locateOnMap(nextAddress);

    // GST often returns a pincode without the state or district (it did for
    // a live lookup of 27AAACR5055K1Z7). The pincode directory knows both;
    // it fills them only where the boxes are still empty.
    const pin = nextAddress.pincode ?? '';
    if (/^\d{6}$/.test(pin) && (!nextAddress.state || !nextAddress.district)) {
      api.get<PincodeLookup>(`/geo/pincodes/${pin}`).then((d) => {
        setAddress((cur) => (cur.pincode !== pin ? cur : {
          ...cur,
          ...(!cur.state && d.state ? { state: d.state } : {}),
          ...(!cur.district && d.district ? { district: d.district } : {}),
        }));
      }).catch(() => { /* typed by hand, as before */ });
    }
  }

  /**
   * Puts the map pin on the address GST returned, so nobody has to search
   * for it by hand. Tried from the most precise wording to the least (the
   * street, then the area and pincode, then the town), because a registry
   * address often names a building the map does not know. Never moves a pin
   * somebody already placed; the operator can drag it if it is off.
   */
  async function locateOnMap(a: AddressFormValue) {
    if (!places.data?.configured || a.latitude || a.longitude) return;
    const join = (parts: Array<string | undefined>) => parts.filter((x) => x && x.trim()).join(', ');
    const tries = [
      join([a.addressLine1, a.locality, a.city, a.pincode]),
      join([a.locality, a.city, a.pincode]),
      join([a.city, a.district, a.state, a.pincode]),
    ].filter((q, i, all) => q.length >= 3 && all.indexOf(q) === i);
    setPinNote('Finding the GST address on the map…');
    for (const q of tries) {
      try {
        const found = await api.get<{ items: Array<{ placeId: string }> }>(`/geo/places?q=${encodeURIComponent(q)}`);
        const first = found.items[0];
        if (!first) continue;
        const d = await api.get<{ latitude: number; longitude: number; placeId: string }>(`/geo/places/${encodeURIComponent(first.placeId)}`);
        setAddress((cur) => (cur.latitude || cur.longitude ? cur
          : { ...cur, latitude: String(d.latitude), longitude: String(d.longitude), placeId: d.placeId }));
        setPinNote('Pin placed from the GST address. Drag it, or click the map, if it is not exactly right.');
        return;
      } catch { /* try the next, shorter wording */ }
    }
    setPinNote('The GST address could not be found on the map. Search for it below, or leave the pin out — it is optional.');
  }

  async function submit() {
    if (busy) return;
    const e = validate();
    setFieldErrors(e);
    setError(null);
    if (Object.keys(e).length) { setScrollTick((t) => t + 1); return; }
    setBusy(true);
    try {
      const created = await api.post<{ id: string; administrator?: { email: string }; invitation?: { expiresAt: string } | { failed: string } | null }>('/tenants', {
        // Activate the company independently of primary-contact and HR access.
        activateNow: true,
        name: form.name.trim(),
        ...(form.legalName.trim() ? { legalName: form.legalName.trim() } : {}),
        ...(currentGstin ? { gstin: currentGstin } : {}),
        // A claim only: the server marks the company GST-verified if it made
        // this lookup itself, for this GSTIN, in the last half hour.
        ...(verified && gst.lookup ? { gstVerification: { gstin: currentGstin, fetchedAt: gst.lookup.fetchedAt } } : {}),
        ...(industry ? { industryId: industry.id } : {}),
        ...(form.workforceCount ? { workforceCount: Number(form.workforceCount) } : {}),
        ...textFields(form, ['contactName', 'contactDesignation', 'contactPhone', 'contactAltPhone', 'contactEmail', 'website']),
        ...addressPayload(address),
      }, idem.key());
      idem.rotate();
      // The company is already committed. Invitation failures are recoverable
      // from its users page and must never resubmit company creation.
      let invitationFailure: string | null = null;
      if (addHr) {
        try {
          const base = `/admin/tenants/${created.id}/setup`;
          const user = await api.post<{ id: string }>(`${base}/users`, {
            fullName: hr.fullName.trim(), email: hr.email.trim().toLowerCase(),
            roleKey: 'HR_ADMIN', scopeIds: [],
          });
          await api.post(`${base}/users/${user.id}/invite`);
        } catch (e) { invitationFailure = errorMessage(e); }
      }
      onDone({
        id: created.id,
        name: form.name.trim(),
        email: addHr && !invitationFailure ? hr.email.trim() : '',
        failed: invitationFailure,
      });
    } catch (e) {
      // A field the server refused is shown where it sits; one this page
      // does not show is named.
      if (e instanceof ApiError && e.hasFieldErrors) {
        const errs = e.fieldErrors;
        setFieldErrors(errs);
        const unseen = Object.keys(errs).filter((f) => !FIELD_SECTION[f] && !(f in address));
        setError(unseen.length
          ? `${errorMessage(e)} (${unseen.map((f) => `${f}: ${errs[f]}`).join('; ')})`
          : errorMessage(e));
        setScrollTick((t) => t + 1);
      } else {
        setError(errorMessage(e));
      }
    } finally { setBusy(false); }
  }

  // What still stops the submit, in words beside the button, as on the lab
  // register form (owner, 30 Oct 2026).
  const missing = [
    form.name.trim().length < 2 && 'a company name',
    form.contactName.trim().length < 2 && 'a contact person',
    !form.contactEmail.trim() && !form.contactPhone && !form.contactAltPhone && 'an email or mobile',
    address.pincode.length !== 6 && 'a pincode',
    addHr && (hr.fullName.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(hr.email)) && 'the HR name and email',
  ].filter(Boolean) as string[];
  const incomplete = missing.length > 0;

  return (
    <Dialog
      title="Register a company"
      info={<>
        Add a company that will send its employees for checkups. Its first plant is created from the pincode, and it
        starts with the two default packages. Fetch from GST to fill most of the form.
      </>}
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <div className="flex w-full flex-wrap items-center justify-end gap-3 sm:flex-nowrap">
          {incomplete && (
            <span id="register-company-missing" className="min-w-0 flex-1 text-caption text-neutral-600 sm:text-right">
              Still needed: {missing.join(', ')}.
            </span>
          )}
          <div className="flex shrink-0 gap-3">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy} disabled={busy || incomplete} aria-describedby={incomplete ? 'register-company-missing' : undefined}>
              Register company
            </Button>
          </div>
        </div>
      )}
    >
      <div ref={formRef}>
        <section aria-labelledby="register-company" className="mt-4">
          <h3 id="register-company" className="text-body font-semibold text-neutral-900">Company</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Field
              label="GSTIN" className="sm:col-span-2" error={fieldErrors.gstin}
              status={gstConfig.loading || gstAvailable ? undefined : 'GST lookup isn’t configured — enter details by hand.'}
            >
              {(p) => (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    {...p}
                    value={form.gstin}
                    onChange={(e) => set('gstin')(e.target.value.toUpperCase().replace(/\s+/g, '').slice(0, 15))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && gstAvailable) { e.preventDefault(); void fetchGst(); } }}
                    placeholder="22AAAAA0000A1Z5"
                    spellCheck={false}
                    autoCapitalize="characters"
                    className="font-mono sm:flex-1"
                  />
                  {gstAvailable && (
                    <Button
                      variant="secondary" onClick={() => void fetchGst()}
                      loading={gst.phase === 'loading'} disabled={gst.phase === 'loading' || !form.gstin}
                    >
                      Fetch details
                    </Button>
                  )}
                </div>
              )}
            </Field>

            {(gst.phase !== 'idle' || stale) && (
              <div className="sm:col-span-2" aria-live="polite">
                {gst.phase === 'loading' && (
                  <p className="text-caption text-neutral-600">Fetching details from GST…</p>
                )}
                {gst.phase === 'error' && gst.error && (
                  <Alert tone={gst.error.kind === 'unavailable' ? 'warning' : 'danger'}>
                    {gst.error.message}
                    {gst.error.kind !== 'unavailable' && ' You can still enter the details by hand.'}
                  </Alert>
                )}
                {gst.phase === 'done' && verified && (
                  <div className="space-y-1.5 rounded-input border border-success-600/25 bg-success-100 px-3 py-2 text-small text-neutral-800">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>Details fetched from GST — review below.</span>
                      {gst.lookup?.gstStatus && (
                        <StatusBadge tone={/^active$/i.test(gst.lookup.gstStatus) ? 'info' : 'warning'}>
                          GST status: {gst.lookup.gstStatus}
                        </StatusBadge>
                      )}
                    </div>
                    {gst.kept.length > 0 && (
                      <p className="text-caption text-neutral-600">Kept your edits for: {gst.kept.join(', ')}.</p>
                    )}
                  </div>
                )}
                {stale && gst.phase !== 'loading' && (
                  <p className="text-caption text-neutral-600">
                    The GSTIN has changed since the details were fetched, so they will be saved as entered by hand.
                    {gstAvailable && ' Fetch again to check this one.'}
                  </p>
                )}
              </div>
            )}

            <TextField
              label="Company name" value={form.name} required
              onChange={set('name')} error={fieldErrors.name} className="sm:col-span-2"
              placeholder="e.g. Tata Steel Ltd"
              hint="The name used everywhere on the platform. A GST fetch suggests the trade name."
            />
            <TextField
              label="Legal name (as per GST)" value={form.legalName} placeholder="As printed on the GST certificate"
              onChange={set('legalName')} error={fieldErrors.legalName}
            />
            <TextField
              label="Number of employees (optional)" value={form.workforceCount} inputMode="numeric" placeholder="e.g. 250"
              onChange={(v) => set('workforceCount')(v.replace(/\D/g, ''))} error={fieldErrors.workforceCount}
            />
            <IndustryCombobox
              label="Industry (optional)"
              value={industry}
              onChange={(i) => { setIndustry(i); setFieldErrors((e) => { const { industryId: _, ...rest } = e; return rest; }); }}
              error={fieldErrors.industryId}
              info="Suggests the health packages to start from in the medical configuration. Can be set later."
            />
          </div>
        </section>

        <section aria-labelledby="register-contact" className="mt-6">
          <h3 id="register-contact" className="flex items-center gap-1 text-body font-semibold text-neutral-900">
            Contact
            <InfoTip label="About the contact person">
              These details are for company communication and do not create a login.
            </InfoTip>
          </h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <TextField label="Contact person" value={form.contactName} required placeholder="e.g. Priya Sharma"
              onChange={set('contactName')} error={fieldErrors.contactName} />
            <TextField label="Designation" value={form.contactDesignation} placeholder="e.g. HR Manager"
              onChange={set('contactDesignation')} error={fieldErrors.contactDesignation} />
            <TextField label="Mobile (+91)" value={form.contactPhone} mono inputMode="numeric" placeholder="98765 43210"
              onChange={(v) => set('contactPhone')(digits10(v))} error={fieldErrors.contactPhone} />
            <TextField label="Alternate mobile (optional)" value={form.contactAltPhone} mono inputMode="numeric" placeholder="98765 43210"
              onChange={(v) => set('contactAltPhone')(digits10(v))} error={fieldErrors.contactAltPhone} />
            <TextField label="Email" value={form.contactEmail} type="email" placeholder="e.g. hr@company.com"
              onChange={set('contactEmail')} error={fieldErrors.contactEmail} />
            <TextField label="Website (optional)" value={form.website} placeholder="https://"
              onChange={set('website')} error={fieldErrors.website} />
          </div>
        </section>

        <section className="mt-6">
          <label className="flex cursor-pointer items-start gap-3 rounded-card border border-neutral-200 bg-neutral-50/60 p-3 text-small hover:bg-neutral-50">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary-600" checked={addHr} onChange={(e) => setAddHr(e.target.checked)} />
            <span>
              <span className="block font-medium text-neutral-900">Add HR access now</span>
              <span className="block text-caption text-neutral-500">Invite the company’s HR to sign in. You can also do this later from the company page.</span>
            </span>
          </label>
          {addHr && <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <TextField label="HR name" value={hr.fullName} required error={fieldErrors.hrName}
              onChange={(fullName) => setHr({ ...hr, fullName })} />
            <TextField label="HR email" value={hr.email} type="email" required error={fieldErrors.hrEmail}
              onChange={(email) => setHr({ ...hr, email })} />
            <p className="sm:col-span-2 text-small">A company-wide HR administrator invitation will be sent to {hr.email || 'the HR email entered above'}. Plant-specific access can be added from company setup.</p>
          </div>}
        </section>

        <section aria-labelledby="register-location" className="mt-6">
          <h3 id="register-location" className="mb-2 flex items-center gap-1 text-body font-semibold text-neutral-900">
            Location
            <InfoTip label="About the location">
              The registered office. The pincode fills in the city and state, and the
              company&rsquo;s first plant is created from it. Filled from GST when you
              fetch the details; the street and the exact map pin are optional.
            </InfoTip>
          </h3>
          {pinNote && <p className="mb-2 rounded bg-primary-50 px-3 py-2 text-caption text-primary-700">{pinNote}</p>}
          <AddressFields
            hideCountry
            pincodeRequired
            value={address}
            onChange={(next) => {
              setAddress(next);
              setFieldErrors((e) => {
                const rest = { ...e };
                for (const k of Object.keys(next)) if (next[k] !== address[k]) delete rest[k];
                return rest;
              });
            }}
            errors={fieldErrors}
          />
        </section>

        {/* What saving does, in the words of what was typed: the summary the
            old confirmation step showed, without the second click. */}
        <p className="mt-6 rounded-input bg-neutral-50 px-3 py-2 text-small text-neutral-700">
          Saving activates the company. HR access can be added separately from company setup. No invitation is sent to the primary contact.
        </p>

        {error && <Alert className="mt-4">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function textFields(form: Record<string, string>, keys: readonly string[]) {
  const out: Record<string, string> = {};
  for (const k of keys) if (form[k]?.trim()) out[k] = form[k].trim();
  return out;
}
